import {skipToken} from '@reduxjs/toolkit/query';
import {
  Button,
  ButtonGroup,
  Card,
  DataStatus,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  OptionType,
} from 'platform/components';
import {Box, Grid, VStack} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';

import {defaultTo, head, isEmpty, isNil, omit} from 'ramda';
import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetTenantQuery, useGetTreeFoldersQuery} from '@dms/api/core';
import {
  MaterialDiscountType,
  PostCustomerContractMaterialDiscountApiArg,
  useGetCustomerContractMaterialDiscountQuery,
  usePatchCustomerContractMaterialDiscountMutation,
  usePostCustomerContractMaterialDiscountMutation,
} from '@dms/api/customer';
import {TreeFolder} from '@dms/api/shared';
import {useGetArticleCategoryListQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  getAllFolders,
  getDecimalFromPercentage,
  getPercentageFromDecimal,
  handleApiError,
} from '@dms/shared';

import {CurrencyCodeType, suffixTestId, TestIdProps} from 'shared/web';

import {DiscountAllowedVariant} from '../../../components/DiscountAllowedVariant';
import {VehicleAgeFields} from '../../../components/VehicleAgeFields';
import {VehicleFields} from '../../../components/VehicleFields';
import {DISCOUNT_DIALOG_BODY_HEIGHT} from '../../../constants/DISCOUNT_DIALOG_BODY_HEIGHT';
import {DiscountFormType, discountFormSchema} from '../../../types/DiscountFormType';

const ARTICLE_CATEGORY_CONTEXT_TARGET = 'spare-parts-catalog';
const FOLDER_PATH_SEPARATOR = ' / ';

interface MaterialDiscountFormProps extends TestIdProps {
  materialDiscountId?: string;
  customerContractId?: string;
  onClose: VoidFunction;
  afterSubmit: VoidFunction;
}

export function MaterialDiscountForm(props: MaterialDiscountFormProps) {
  const formatCurrencySymbol = useCurrencySymbolFormatter();

  const {data: tenant} = useGetTenantQuery();
  const {data, isLoading, isError} = useGetCustomerContractMaterialDiscountQuery(
    {
      customerContractId: props.customerContractId ?? '',
      materialDiscountId: props.materialDiscountId ?? '',
    },
    {skip: isNil(props.materialDiscountId)}
  );

  const {data: articleCategoryList, isLoading: isArticleCategoryListLoading} =
    useGetArticleCategoryListQuery();
  const articleCategoryIds = articleCategoryList?.categories?.map((category) => category.id) ?? [];
  const {data: treeFolders, isLoading: isTreeFoldersLoading} = useGetTreeFoldersQuery(
    isNotNilOrEmpty(articleCategoryIds)
      ? {contextTarget: ARTICLE_CATEGORY_CONTEXT_TARGET, body: {contextIds: articleCategoryIds}}
      : skipToken
  );

  const [postMaterialDiscount] = usePostCustomerContractMaterialDiscountMutation();
  const [patchMaterialDiscount] = usePatchCustomerContractMaterialDiscountMutation();

  const handleSubmit: FormSubmitHandler<DiscountFormType> = async (data, setErrors) => {
    const selectedTreeFolderIds = data.treeFolderIds ?? [];
    // The backend rejects a folder deleted from the catalogue, and the field cannot show it.
    const treeFolderIds = isNil(treeFolders)
      ? selectedTreeFolderIds
      : getExistingTreeFolderIds(selectedTreeFolderIds, treeFolders);

    // An empty list applies the discount to all articles, so never widen the rule silently.
    if (isEmpty(treeFolderIds) && isNotNilOrEmpty(selectedTreeFolderIds)) {
      setErrors([
        {
          name: 'treeFolderIds',
          message: i18n.t('entity.customerContract.validations.articleCategoriesDeleted'),
        },
      ]);
      return;
    }

    const body: PostCustomerContractMaterialDiscountApiArg['body'] = {
      ...omit(['vehicleType', 'vehicleMake'], data),
      name: defaultTo('', data.name),
      vehicleTypes: data.vehicleType ? [data.vehicleType] : undefined,
      vehicleMakes: data.vehicleMake ? [data.vehicleMake] : undefined,
      directSaleVariantIds: data.isDirectSaleVariant ? data.directSaleVariantIds : undefined,
      serviceOrderVariantIds: data.isServiceOrderVariant ? data.serviceOrderVariantIds : undefined,
      sparePartManufacturers: data.sparePartManufacturers ?? undefined,
      // PATCH replaces the whole list, so a missing field would delete the saved categories.
      treeFolderIds,
      discountSettingsValue: getDecimalFromPercentage(data.discountSettingsValue),
    };

    await (
      isNotNil(props.materialDiscountId)
        ? patchMaterialDiscount({
            customerContractId: props.customerContractId ?? '',
            materialDiscountId: props.materialDiscountId,
            body,
          })
        : postMaterialDiscount({
            customerContractId: props.customerContractId ?? '',
            body,
          })
    )
      .unwrap()
      .then(() => {
        props.onClose();
        props.afterSubmit();
      })
      .catch(handleApiError);
  };

  const defaultValues: Partial<DiscountFormType> = {
    ...data,
    name: data?.name ?? '',
    vehicleType: head(data?.vehicleTypes ?? []),
    vehicleMake: head(data?.vehicleMakes ?? []),
    vehicleAgeType: data?.vehicleAgeType ?? 'VEHICLE_AGE_UNDECIDED',
    isServiceOrderVariant: defaultTo(true)(data?.isServiceOrderVariant),
    isDirectSaleVariant: defaultTo(true)(data?.isDirectSaleVariant),
    sparePartManufacturers: data?.sparePartManufacturers ?? [],
    // GET leaves the field out when no category is set.
    treeFolderIds: data?.treeFolderIds ?? [],
    discountSettingsType: data?.discountSettingsType ?? 'DISCOUNT_CUSTOMER_GROUP',
    discountSettingsValue:
      isNotNil(data) && isNotNil(data?.discountSettingsValue)
        ? getPercentageFromDecimal(data.discountSettingsValue)
        : undefined,
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={DISCOUNT_DIALOG_BODY_HEIGHT}>
      <Form<DiscountFormType>
        onSubmit={handleSubmit}
        experimentalZodSchema={discountFormSchema}
        defaultValues={defaultValues}
      >
        {(control, formApi) => (
          <>
            <Box height={[undefined, undefined, DISCOUNT_DIALOG_BODY_HEIGHT]}>
              <VStack spacing={4}>
                <FormField
                  control={control}
                  name="name"
                  type="text"
                  label={i18n.t('general.labels.name')}
                  isRequired
                  data-testid={suffixTestId('name', props)}
                />
                <Grid columns={[1, 1, 2]} spacing={6}>
                  <VStack spacing={3}>
                    <Grid columns={[1, 2]} spacing={4} verticalSpacing={3}>
                      <VehicleFields
                        control={control}
                        formApi={formApi}
                        data-testid={props['data-testid']}
                        showSparePartManufacturers
                      />
                      <FormField
                        control={control}
                        name="treeFolderIds"
                        type="multiChoice"
                        label={i18n.t('entity.warehouse.labels.articleCategories')}
                        options={getArticleCategoryOptions(treeFolders ?? [])}
                        isLoading={isArticleCategoryListLoading || isTreeFoldersLoading}
                        data-testid={suffixTestId('treeFolderIds', props)}
                        closeMenuOnSelect={false}
                      />
                    </Grid>
                    <VehicleAgeFields
                      control={control}
                      formApi={formApi}
                      data-testid={props['data-testid']}
                    />
                  </VStack>
                  <VStack spacing={4}>
                    <DiscountAllowedVariant
                      control={control}
                      data-testid={suffixTestId('discountAllowedVariant', props)}
                    />
                    <Card
                      variant="inlineGrey"
                      title={i18n.t('entity.workshopCustomerGroup.labels.discountSettings')}
                    >
                      <Grid columns={[1, 1, 2]} spacing={4} align="center">
                        <FormField
                          control={control}
                          name="discountSettingsType"
                          type="radio"
                          options={getDiscountOptions(
                            formatCurrencySymbol(tenant?.currency as CurrencyCodeType)
                          )}
                          direction="column"
                          spacing={3}
                          data-testid={suffixTestId('discountSettingsType', props)}
                        />
                        <FormField
                          control={control}
                          name="discountSettingsValue"
                          type="number"
                          isStepperVisible
                          minStepperValue={0}
                          maxStepperValue={
                            formApi.watch('discountSettingsType') === 'PERCENTAGE_DISCOUNT'
                              ? 100
                              : undefined
                          }
                          decimalPlaces={2}
                          helperText={i18n.t('entity.customerContract.labels.discountDescription')}
                          data-testid={suffixTestId('discountSettingsValue', props)}
                        />
                      </Grid>
                    </Card>
                  </VStack>
                </Grid>
              </VStack>
            </Box>
            <DialogFooter>
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.discard')}
                  variant="secondary"
                  onClick={props.onClose}
                  data-testid={suffixTestId('discard', props)}
                />
                <FormButton
                  control={control}
                  type="submit"
                  title={
                    props.materialDiscountId
                      ? i18n.t('general.actions.saveChanges')
                      : i18n.t('general.actions.create')
                  }
                  // The deleted folder check needs the loaded folders.
                  isDisabled={isArticleCategoryListLoading || isTreeFoldersLoading}
                  data-testid={suffixTestId('submit', props)}
                />
              </ButtonGroup>
            </DialogFooter>
          </>
        )}
      </Form>
    </DataStatus>
  );
}

const getDiscountOptions = (currency: string): OptionType<MaterialDiscountType>[] => [
  {
    label: i18n.t('entity.customerContract.labels.discountByCustomerGroup', {currency}),
    value: 'DISCOUNT_CUSTOMER_GROUP',
  },
  {
    label: i18n.t('entity.customerContract.labels.discountOnSalePrice'),
    value: 'DISCOUNT_SALE_PRICE',
  },
  {
    label: i18n.t('entity.customerContract.labels.marginToPurchasePrice'),
    value: 'DISCOUNT_MARGIN_PURCHASE_PRICE',
  },
];

const getArticleCategoryOptions = (folders: TreeFolder[]): OptionType[] =>
  getAllFolders(folders).flatMap((folder) =>
    isNil(folder.id)
      ? []
      : [
          {
            value: folder.id,
            label: [...folder.parents, folder]
              .map((pathFolder) => pathFolder.label ?? '')
              .join(FOLDER_PATH_SEPARATOR),
          },
        ]
  );

const getExistingTreeFolderIds = (treeFolderIds: string[], folders: TreeFolder[]) => {
  const existingIds = getAllFolders(folders).map((folder) => folder.id);
  return treeFolderIds.filter((id) => existingIds.includes(id));
};
